import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Divider,
  Paper,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { formatJpDateTime } from "../../utils/dateUtils";
import ConfirmStopModal from "../../shared/ConfirmStopModal";
import RequestStatus from "../../shared/RequestStatus";
import RequestDetailActionButton from "../../shared/RequestDetailActionButton";

const ProjectRequestDetail = ({
  userContractRequestInfo,
  userContractRequest,
  viewerType,
}) => {
  const [isActive, setIsActive] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const hasRequest = !!userContractRequest?.id;
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("sm"));

  // textareaの改行設定
  const preLineLabels = ["備考"];

  useEffect(() => {
    if (typeof userContractRequest?.recruiting_flag === "boolean") {
      setIsActive(userContractRequest.recruiting_flag);
    }
  }, [userContractRequest]);

  const updateRecruitingFlag = (newStatus) => {
    fetch("/user_contract_request/toggle_recruiting", {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")
          .content,
      },
      body: JSON.stringify({ recruiting_flag: newStatus }),
    }).then((res) => {
      if (res.ok) {
        setIsActive(newStatus);
      } else {
        alert("ステータスの更新に失敗しました。");
      }
    });
  };

  const toggleStatus = useCallback(() => {
    if (isActive) {
      setModalOpen(true);
    } else {
      updateRecruitingFlag(true);
    }
  }, [isActive, setModalOpen, updateRecruitingFlag]);

  const handleConfirmStop = () => {
    updateRecruitingFlag(false);
    setModalOpen(false);
  };

  const handleCancelStop = () => {
    setModalOpen(false);
  };

  const prioritySummary = userContractRequestInfo?.priority_summary ?? [];

  const rows = useMemo(
    () => [
      {
        label: "希望金額",
        value: userContractRequest?.desired_amount
          ? `${userContractRequest.desired_amount}万円`
          : "未設定",
      },
      {
        label: "開始希望日",
        value: userContractRequest?.start_date
          ? new Date(userContractRequest.start_date).toLocaleDateString("ja-JP")
          : "未設定",
      },
      {
        label: "常駐可否",
        value:
          typeof userContractRequestInfo?.work_style === "string"
            ? userContractRequestInfo.work_style
            : "未設定",
      },
      {
        label: "得意スキル",
        value: userContractRequest?.technical_skill ?? "未設定",
      },
      {
        label: "ヒューマンスキル",
        value: userContractRequestInfo ? (
          isDesktop ? (
            <ul style={{ margin: 0 }}>
              <li>
                問題解決能力 :{" "}
                {userContractRequestInfo.problem_solving ?? "未設定"}
              </li>
              <li>
                コミュニケーション能力 :{" "}
                {userContractRequestInfo.communication_skill ?? "未設定"}
              </li>
              <li>
                自律性と責任感 :{" "}
                {userContractRequestInfo.autonomy_and_responsibility ??
                  "未設定"}
              </li>
              <li>
                学習意欲と成長 :{" "}
                {userContractRequestInfo.learning_and_growth ?? "未設定"}
              </li>
              <li>
                チームへの貢献度 :{" "}
                {userContractRequestInfo.team_contribution ?? "未設定"}
              </li>
            </ul>
          ) : (
            <Box
              component="div"
              sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
            >
              <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                問題解決能力 :{" "}
                {userContractRequestInfo.problem_solving ?? "未設定"}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                コミュニケーション能力 :{" "}
                {userContractRequestInfo.communication_skill ?? "未設定"}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                自律性と責任感 :{" "}
                {userContractRequestInfo.autonomy_and_responsibility ??
                  "未設定"}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                学習意欲と成長 :{" "}
                {userContractRequestInfo.learning_and_growth ?? "未設定"}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                チームへの貢献度 :{" "}
                {userContractRequestInfo.team_contribution ?? "未設定"}
              </Typography>
            </Box>
          )
        ) : (
          "未設定"
        ),
      },
      { label: "備考", value: userContractRequest?.remark ?? "未設定" },
    ],
    [userContractRequest, userContractRequestInfo, isDesktop, prioritySummary]
  );

  return (
    <Paper
      sx={{
        borderRadius: 2,
        p: { xs: 2, sm: 4 },
      }}
    >
      <Typography
        variant="h5"
        fontWeight="bold"
        gutterBottom
        sx={{
          fontSize: "clamp(0.7rem, 4vw, 1.8rem)",
          alignItems: "left",
          lineHeight: 1.5,
          whiteSpace: "nowrap",
          textAlign: { xs: "center", sm: "left" },
        }}
      >
        フリーランスの新規プロジェクト、副業探し
      </Typography>

      {/* ステータス */}
      <RequestStatus
        viewerType={viewerType}
        type="project"
        isActive={isActive}
        hasRequest={hasRequest}
        onToggle={toggleStatus}
      />

      {/* 確認用モーダル */}
      <ConfirmStopModal
        open={modalOpen}
        onClose={handleCancelStop}
        onConfirm={handleConfirmStop}
        title="プロジェクトの募集停止"
        message="募集を停止するとプロジェクト情報の閲覧が不可能となります。"
        confirmLabel="募集を停止する"
      />

      {/* ユーザーにのみ表示 */}
      {viewerType !== "company" &&
        (hasRequest ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 3,
              fontSize: { xs: "0.8rem", sm: "0.875rem" },
            }}
          >
            {isActive ? (
              <>
                募集要項とあなたのスキルシート詳細画面は<strong>公開中</strong>
                です。
                <br />
                新規プロジェクト受付中の公開URLではプロジェクト一覧を閲覧することが可能です。
                <br />
                受付をOFFにするとプロジェクトの閲覧が不可になります。ご注意ください。
              </>
            ) : (
              <>
                現在、プロジェクト受付は<strong>停止中</strong>です。
                <br />
                募集要項とあなたのスキルシート詳細画面は企業側には表示されません。
                <br />
                受付をONにすると、企業があなたの情報を閲覧できるようになります。
              </>
            )}
          </Typography>
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 3,
              fontSize: { xs: "0.8rem', sm: '0.875rem" },
            }}
          >
            募集要項がまだ作成されていません。
            <br />
            「編集する」ボタンから、まずは募集要項を登録してください。
          </Typography>
        ))}

      <Divider sx={{ mb: 3 }} />

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Typography variant="h6">希望条件</Typography>

        {/* ユーザーにのみ表示 */}
        {viewerType !== "company" && (
          <Button
            variant="text"
            size="small"
            sx={{
              mb: 0.5,
              color: "#000",
              textTransform: "none",
              transition: "opacity 0.2s ease-in-out",
              "&:hover": { opacity: 0.7 },
              "&:focus": { opacity: 0.7 },
            }}
            onClick={() =>
              (window.location.href = "/user_contract_request/edit")
            }
          >
            編集する
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="#8b5cf6"
              viewBox="0 0 24 24"
            >
              <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1.003 1.003 0 000-1.42l-2.34-2.34a1.003 1.003 0 00-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z" />
            </svg>
          </Button>
        )}
      </Box>

      <Typography
        variant="caption"
        color="text.disabled"
        sx={{ mb: 2, display: "block" }}
      >
        更新日時：{formatJpDateTime(userContractRequest?.updated_at)}
      </Typography>

      {isDesktop ? (
        <Table size="small" sx={{ mb: 4, border: "1px solid #ccc" }}>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={index}>
                <TableCell
                  align="right"
                  sx={{
                    backgroundColor: index % 2 === 0 ? "#4b5563" : "#9ca3af",
                    color: "white",
                    width: "25%",
                  }}
                >
                  {row.label}
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: index % 2 === 0 ? "#f3f4f6" : "#f9fafb",
                    whiteSpace: preLineLabels.includes(row.label)
                      ? "pre-line"
                      : undefined,
                    wordBreak: preLineLabels.includes(row.label)
                      ? "break-all"
                      : undefined,
                  }}
                >
                  {row.value}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Table size="small" sx={{ mb: 4, border: "1px solid #ccc" }}>
          <TableBody>
            {rows.map((row, index) => (
              <React.Fragment key={index}>
                <TableRow>
                  <TableCell
                    colSpan={2}
                    sx={{
                      backgroundColor: index % 2 === 0 ? "#4b5563" : "#9ca3af",
                      color: "white",
                      py: 1,
                      fontSize: "0.8rem",
                    }}
                  >
                    {row.label}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    colSpan={2}
                    sx={{
                      backgroundColor: index % 2 === 0 ? "#f3f4f6" : "#f9fafb",
                      whiteSpace: preLineLabels.includes(row.label)
                        ? "pre-line"
                        : undefined,
                      wordBreak: preLineLabels.includes(row.label)
                        ? "break-all"
                        : undefined,
                      fontSize: "0.8rem",
                    }}
                  >
                    {row.value}
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      )}

      {/* プロジェクト一覧に遷移 */}
      <RequestDetailActionButton
        viewerType={viewerType}
        hasRequest={hasRequest}
        isActive={isActive}
        buttonLabel="プロジェクトを探してみる →"
        buttonUrl="/projects"
        tooltip="現在プロジェクト受付を停止しています"
      />
    </Paper>
  );
};

export default ProjectRequestDetail;
